// @ts-check
import React, { useState, useCallback, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Tabs, TabList, TabPanels, Tab, TabPanel, VStack } from "@chakra-ui/react";
import { NumberParam, useQueryParam } from "use-query-params";

import { useFlowConfig } from "flows/hooks";

import {
  INITIAL_PENDING_SELECTED_TYPES,
  INITIAL_COMPLETED_SELECTED_TYPES,
  processFlowConfigInitialFilters,
} from "../utils";
import Header from "flows/components/Header";
import AccountSelector from "./AccountSelector";
import CompletedTab from "../tabs/CompletedTab";
import PendingTab from "../tabs/PendingTab";

import useTransactionFilters from "../../hooks/useTransactionFilters";

const { COMPLETED_TAB, PENDING_TAB } = {
  COMPLETED_TAB: "COMPLETED_TAB",
  PENDING_TAB: "PENDING_TAB",
};

export default function TransactionHistoryRoute({
  onClose: handleCloseButtonClicked,
  selectedAccountId,
  onSelectAccountId,
}) {
  const { formatMessage } = useIntl();
  const { hideExitButtons, txnHistoryTabIndex, txnHistoryFiltersStr, hideMergedAccountSelector } = useFlowConfig();
  const [tabIndex, setTabIndex] = useQueryParam("tab", NumberParam);
  const initialCompletedFilters = processFlowConfigInitialFilters(INITIAL_COMPLETED_SELECTED_TYPES, txnHistoryFiltersStr);
  const initialPendingFilters = processFlowConfigInitialFilters(INITIAL_PENDING_SELECTED_TYPES, txnHistoryFiltersStr);
  const expandFiltersForCompleted = initialCompletedFilters.reduce((acc, currVal) => (acc || currVal.selected), false);
  const expandFiltersForPending = initialPendingFilters.reduce((acc, currVal) => (acc || currVal.selected), false);
  const [expandFilters, setExpandFilters] = useState({
    [COMPLETED_TAB]: expandFiltersForCompleted,
    [PENDING_TAB]: expandFiltersForPending,
  });
  const isFirstCallForFlowConfigTabIndexCheck = useRef(true);

  useEffect(() => {
    // useEffect for handling flowConfig.txnHistoryTabIndex value
    if (isFirstCallForFlowConfigTabIndexCheck.current) {
      isFirstCallForFlowConfigTabIndexCheck.current = false;
      const flowConfigTabIsSet = [0,1,"0","1"].includes(txnHistoryTabIndex); // txn history tab index values
      if (flowConfigTabIsSet && tabIndex !== txnHistoryTabIndex) setTabIndex(txnHistoryTabIndex);
    }
  }, [txnHistoryTabIndex, setTabIndex, tabIndex]);

  /**
   * Data shape is designed to be extensible and potentially manage state for
   * other Transaction History API params
   */
  const { filterSets, toggleTypeBySet } = useTransactionFilters({
    [COMPLETED_TAB]: {
      types: initialCompletedFilters,
    },
    [PENDING_TAB]: {
      types: initialPendingFilters,
    },
  });

  const toggleShowFilters = useCallback(
    (tab) => {
      setExpandFilters((expandFilters) => ({
        ...expandFilters,
        [tab]: !expandFilters[tab],
      }));
    },
    [setExpandFilters]
  );

  const toggleCompletedTransactionType = useCallback(
    (type) => {
      toggleTypeBySet(COMPLETED_TAB, type);
    },
    [toggleTypeBySet]
  );

  const togglePendingTransactionType = useCallback(
    (type) => {
      toggleTypeBySet(PENDING_TAB, type);
    },
    [toggleTypeBySet]
  );

  const toggleExpandCompletedFilters = useCallback(
    () => toggleShowFilters(COMPLETED_TAB),
    [toggleShowFilters]
  );

  const toggleExpandPendingFilters = useCallback(
    () => toggleShowFilters(PENDING_TAB),
    [toggleShowFilters]
  );

  return (
    <VStack spacing={4} align="stretch">
      <Header
        showLanguage
        title={formatMessage({
          defaultMessage: "Transaction history",
          description: "Header title",
        })}
        onCloseButtonClick={!hideExitButtons ? handleCloseButtonClicked : undefined}
      />

      <Tabs isFitted index={tabIndex || 0} onChange={setTabIndex} variant="line" size="sm" isLazy>
        {!hideMergedAccountSelector ? (
          <Box mb="4">
            <AccountSelector value={selectedAccountId} onChange={onSelectAccountId} />
          </Box>
        ) : null}
        <TabList>
          <Tab>
            <FormattedMessage defaultMessage="Completed" description="Tab label" />
          </Tab>
          <Tab>
            <FormattedMessage defaultMessage="Pending" description="Tab label" />
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel padding="0px">
            <CompletedTab
              accountId={selectedAccountId}
              filters={filterSets[COMPLETED_TAB]}
              toggleTxnType={toggleCompletedTransactionType}
              expandFilters={expandFilters[COMPLETED_TAB]}
              onClickFilterButton={toggleExpandCompletedFilters}
            />
          </TabPanel>
          <TabPanel padding="0px">
            <PendingTab
              accountId={selectedAccountId}
              filters={filterSets[PENDING_TAB]}
              toggleTxnType={togglePendingTransactionType}
              expandFilters={expandFilters[PENDING_TAB]}
              onClickFilterButton={toggleExpandPendingFilters}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
}
